/**
 * Drop-in replacement for @jamal./gatsby-theme-shopify-manager in the showcase build.
 *
 * The original theme kept a Shopify checkout (shopify-buy) in context. The store is
 * closed, so this keeps the same hook API but holds the cart locally (localStorage)
 * and builds line items from data/catalog.js. Checkout leads to the showcase page.
 */
import React, { createContext, useContext, useEffect, useState } from "react"
import { variantsById } from "../../data/catalog"

const STORAGE_KEY = "showcase-cart"
export const CHECKOUT_URL = "/showcase/"

const CartContext = createContext(null)

function loadItems() {
  if (typeof window === "undefined") return []
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || []
  } catch {
    return []
  }
}

// shape a stored item like a shopify-buy checkout line item
function toLineItem({ variantId, quantity, customAttributes = [] }) {
  const entry = variantsById[variantId]
  if (!entry) return null
  const { product, variant } = entry
  return {
    id: variantId,
    title: product.title,
    quantity,
    customAttributes,
    discountAllocations: [],
    variant: {
      id: variant.shopifyId,
      title: variant.title,
      price: variant.price,
      compareAtPrice: variant.compareAtPrice,
      unitPrice: variant.unitPrice,
      image: { src: product.images[0].originalSrc },
      product: { id: product.storefrontId },
    },
  }
}

function buildCart(items) {
  const lineItems = items.map(toLineItem).filter(Boolean)
  const total = lineItems.reduce((sum, item) => sum + parseFloat(item.variant.price) * item.quantity, 0)
  return {
    lineItems,
    totalPrice: total.toFixed(2),
    subtotalPrice: total.toFixed(2),
    discountApplications: [],
    note: null,
    webUrl: CHECKOUT_URL,
  }
}

export function ContextProvider({ children }) {
  const [items, setItems] = useState([])

  useEffect(() => {
    setItems(loadItems())
  }, [])

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
    } catch {}
  }, [items])

  function addItems(newItems) {
    setItems(current => {
      const next = [...current]
      newItems.forEach(({ variantId, quantity = 1, customAttributes = [] }) => {
        if (!variantsById[variantId]) return
        const existing = next.find(item => item.variantId === variantId)
        if (existing) existing.quantity += quantity
        else next.push({ variantId, quantity, customAttributes })
      })
      return next.map(item => ({ ...item }))
    })
  }

  const value = {
    items,
    cart: buildCart(items),
    addItems,
    updateQuantity: (variantId, quantity) =>
      setItems(current =>
        quantity <= 0
          ? current.filter(item => item.variantId !== variantId)
          : current.map(item => (item.variantId === variantId ? { ...item, quantity } : item))
      ),
    removeItems: variantIds => setItems(current => current.filter(item => !variantIds.includes(item.variantId))),
  }

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

function useCartContext() {
  const context = useContext(CartContext)
  if (!context) throw new Error("Showcase cart hooks must be used inside ContextProvider")
  return context
}

export const useCart = () => useCartContext().cart
export const useCartItems = () => useCartContext().cart.lineItems
export const useCartCount = () => useCartContext().items.reduce((sum, item) => sum + item.quantity, 0)
export const useCheckoutUrl = () => CHECKOUT_URL
export const useGetLineItem = () => {
  const { cart } = useCartContext()
  return variantId => cart.lineItems.find(item => item.variant.id === variantId) || null
}

export function useAddItemsToCart() {
  const { addItems } = useCartContext()
  return async items => addItems(items)
}

export function useAddItemToCart() {
  const { addItems } = useCartContext()
  return async (variantId, quantity = 1, customAttributes = []) => addItems([{ variantId, quantity, customAttributes }])
}

export function useUpdateItemQuantity() {
  const { updateQuantity } = useCartContext()
  return async (variantId, quantity) => updateQuantity(variantId, quantity)
}

export function useRemoveItemFromCart() {
  const { removeItems } = useCartContext()
  return async variantId => removeItems([variantId])
}

export function useRemoveItemsFromCart() {
  const { removeItems } = useCartContext()
  return async variantIds => removeItems(variantIds)
}

// discounts and notes only existed on the Shopify checkout
export const useAddDiscountToCart = () => async () => {}
export const useSetCheckoutNote = () => async () => {}
